// noinspection GrazieInspection, AiaStyle

import {Injectable} from "@angular/core";
import {ReplaySubject, Subject} from "rxjs";
import {AuthService, LocalConfigurationService, LoginType} from "@fedelta-pos/data-client";
import {Local} from "./config";
import {Device} from "@ionic-native/device";
import {BeaconData, BeaconOrderType, BeaconSearchModel} from "../beacon/beacon-search.model";
import {IdentityModel} from "../identity/identity.model";
import {StoreData} from "../store/store-search.model";
import {OrderType} from "../order/order";

export class ParamConfig {
    parentAppURL: string;
    store: number;
    exclusiveStore: number;
    stores: number[];
    exclusiveStores: number[];
    storeGroup: string;
    exclusiveStoreGroup: string;
    orderType: OrderType;
    tableNumber: string;
    tableOnlyMode: boolean;
    pickupOnlyMode: boolean;
    deliveryOnlyMode: boolean;
    showExitButton: boolean;
    scanId: string;
    beaconId: string;
    unknownBeacon: boolean;
    beaconScanTime: number;
    dealCode: string;
    nativeMode: boolean;
    waiterMode: boolean;
    noAutoScroll: boolean;
    scanType: string;
    forcePay: string;
    forceTyroWalletButtons: boolean;
    defaultLocalDateTime: string;
    disableLocationServices: boolean;
    useStoreCoords: boolean;
    latitude: number;
    longitude: number;

    /**
     * Creates a config for an external store route
     */
    cloneForExternalStore(): ParamConfig {
        const config: ParamConfig = new ParamConfig();
        config.exclusiveStore = this.exclusiveStore;
        config.exclusiveStoreGroup = this.exclusiveStoreGroup;
        config.exclusiveStores = this.exclusiveStores;
        config.orderType = this.orderType;
        config.tableNumber = this.tableNumber;
        config.tableOnlyMode = this.tableOnlyMode;
        config.deliveryOnlyMode = this.deliveryOnlyMode;
        config.pickupOnlyMode = this.pickupOnlyMode;
        config.showExitButton = this.showExitButton;
        config.beaconId = this.beaconId;
        config.beaconScanTime = this.beaconScanTime;
        config.dealCode = this.dealCode;
        config.nativeMode = this.nativeMode;
        config.waiterMode = this.waiterMode;
        config.noAutoScroll = this.noAutoScroll;
        config.scanType = this.scanType;
        config.forcePay = this.forcePay;
        config.forceTyroWalletButtons = this.forceTyroWalletButtons;
        config.defaultLocalDateTime = this.defaultLocalDateTime;
        config.disableLocationServices = this.disableLocationServices;
        config.useStoreCoords = this.useStoreCoords;
        config.latitude = this.latitude;
        config.longitude = this.longitude;
        return config;
    }

    static standardiseLocalDateTime(localDateTime: string): string {
        return !localDateTime ? undefined
            : localDateTime.length === 10 ? localDateTime + "T00:00:00"
                : localDateTime.length === 13 ? localDateTime + ":00:00"
                    : localDateTime.length === 16 ? localDateTime + ":00"
                        : localDateTime;
    }
}

/**
 * To be called as an app initializer function to ensure URL params are resolved before the app proceeds.
 * @param paramConfigService
 */
export function initParamConfigService(paramConfigService: ParamConfigService): (() => Promise<void>) {
    return async () => await paramConfigService.init();
}

/**
 * Config from the URL parameters
 */
@Injectable()
export class ParamConfigService {

    private config$: Subject<ParamConfig> = new ReplaySubject(1);

    // Avoid injection dependency on IdentityModel
    private _identityModel: IdentityModel;

    constructor(private beaconSearchModel: BeaconSearchModel,
                private authService: AuthService,
                private localConfigurationService: LocalConfigurationService,
                private device: Device) {
        console.log("--- ParamConfigService");
    }

    set identityModel(identityModel: IdentityModel) {
        this._identityModel = identityModel;
    }

    /**
     * Parses the app URL to get config params.
     */
    async init(): Promise<void> {
        let config: ParamConfig;
        const urlParams: URLSearchParams = new URLSearchParams(window.location.search);

        if (urlParams.has("c")) {
            // base64 encoded JSON config from the beacon lookup service, e.g. {"tableNumber": 22, "stores": [1,2], "tableOnlyMode": true}
            config = Object.assign(new ParamConfig(), JSON.parse(atob(urlParams.get("c"))));
            config.defaultLocalDateTime = ParamConfig.standardiseLocalDateTime(config.defaultLocalDateTime);
            this.authService.setDefaultLocalDateTime(config.defaultLocalDateTime);
        } else {
            // Cherry-pick config
            config = new ParamConfig();

            if (urlParams.has("id")) {
                config.beaconId = urlParams.get("id");
            }

            // Get scan Id
            if (urlParams.has("sid")) {
                config.scanId = urlParams.get("sid");
            }

            if (urlParams.has("bst")) {
                config.beaconScanTime = parseInt(urlParams.get("bst"));
            }

            if (urlParams.has("parentAppURL")) {
                config.parentAppURL = urlParams.get("parentAppURL");
            }

            if (urlParams.has("scan")) {
                config.scanType = urlParams.get("scan");
            }

            if (urlParams.has("forcePay")) {
                config.forcePay = urlParams.get("forcePay");
            }
            config.forceTyroWalletButtons = urlParams.has("forceTyroWalletButtons");

            if (urlParams.has("defaultLocalDateTime")) {
                config.defaultLocalDateTime = ParamConfig.standardiseLocalDateTime(urlParams.get("defaultLocalDateTime"));
                this.authService.setDefaultLocalDateTime(config.defaultLocalDateTime);
            }

            config.disableLocationServices = config.nativeMode;
            if (urlParams.has("useStoreCoords")) {
                config.useStoreCoords = true;
            } else if (urlParams.has("latitude") && urlParams.has("longitude")) {
                config.latitude = parseInt(urlParams.get("latitude"));
                config.longitude = parseInt(urlParams.get("longitude"));
            }

            if (urlParams.has("exclusiveStore")) {
                config.exclusiveStore = parseInt(urlParams.get("exclusiveStore"));
            } else if (urlParams.has("singleStore")) { // backward compatibility
                config.exclusiveStore = parseInt(urlParams.get("singleStore"));
            } else {
                if (urlParams.has("store")) {
                    config.store = parseInt(urlParams.get("store"));
                }

                if (urlParams.has("exclusiveStoreGroup")) {
                    config.exclusiveStoreGroup = urlParams.get("exclusiveStoreGroup");
                } else if (urlParams.has("exclusiveStores")) {
                    config.exclusiveStores = [];
                    const stores: string[] = urlParams.get("exclusiveStores").split(",");
                    stores.forEach((s: string) => config.exclusiveStores.push(parseInt(s)));
                } else if (urlParams.has("storeGroup")) {
                    config.storeGroup = urlParams.get("storeGroup");
                } else if (urlParams.has("stores")) {
                    config.stores = [];
                    const stores: string[] = urlParams.get("stores").split(",");
                    stores.forEach((s: string) => config.stores.push(parseInt(s)));
                }
            }

            if (urlParams.has("orderType")) {
                switch (urlParams.get("orderType")) {
                    case "pickup":
                        config.orderType = OrderType.ORDER_AHEAD;
                        break;
                    case "delivery":
                        config.orderType = OrderType.ORDER_DELIVERY;
                        break;
                    case "table":
                        config.orderType = OrderType.ORDER_AT_TABLE;
                        break;
                }
            }

            if (urlParams.has("tableNumber")) {
                config.tableNumber = urlParams.get("tableNumber");
            }

            config.tableOnlyMode = urlParams.has("tableOnlyMode");
            config.pickupOnlyMode = urlParams.has("pickupOnlyMode");
            config.deliveryOnlyMode = urlParams.has("deliveryOnlyMode");

            config.showExitButton = urlParams.get("showExitButton") === "true";

            if (urlParams.has("dealCode")) {
                config.dealCode = urlParams.get("dealCode");
            }

            config.nativeMode = urlParams.has("nativeMode");
            config.waiterMode = urlParams.has("waiterMode");
            config.noAutoScroll = urlParams.has("noAutoScroll");
        }

        if (config.beaconId && !config.parentAppURL) {
            await this.lookupBeacon(config);
        }

        // Table ordering is disabled for native mode.
        // In the absence of specific order type limit params, native mode implies pickup and delivery only.
        if (config.nativeMode) {
            config.tableOnlyMode = false;
            config.tableNumber = undefined;
            if (config.orderType == OrderType.ORDER_AT_TABLE) {
                config.orderType = undefined;
            }
            if (!config.pickupOnlyMode && !config.deliveryOnlyMode) {
                config.pickupOnlyMode = true;
                config.deliveryOnlyMode = true;
            }
        }

        if (config.waiterMode) {
            config.tableOnlyMode = true;
            config.nativeMode = true;
            config.orderType = undefined;
            config.pickupOnlyMode = false;
            config.deliveryOnlyMode = false;
        }

        if (urlParams.has("m") && window.location.href.indexOf("&m=") > -1) {

            // Strip off the m param which should be on the end
            history.replaceState("active", "", window.location.href.substring(0, window.location.href.indexOf("&m=")));

            // noinspection ES6MissingAwait
            this._identityModel.loginAsExternalMember(urlParams.get("m"));
        }

        console.log(config);
        this.config$.next(config);
    }

    async lookupBeacon(config: ParamConfig): Promise<void> {
        try {
            const loggedIn: boolean = this.authService.isLoggedIn();
            if (!loggedIn) {
                // Temporarily login so the beacon data can be retrieved
                const password: string = await this.localConfigurationService.getString(Local.LOGIN_TOKEN);
                const appId: number = await this.localConfigurationService.getNumber(Local.APP_ID);
                await this.authService.login("fedelta-app-token", password, LoginType.GUEST, {
                    appId: appId,
                    deviceId: this.device.uuid
                }).first().toPromise();
            }
            if (this.authService.isLoggedIn()) {
                const beacon: BeaconData = await this.beaconSearchModel.lookupBeacon(config.beaconId);
                if (beacon) {
                    config.exclusiveStore = beacon.appStore;
                    config.exclusiveStoreGroup = beacon.appStoreGroup;
                    config.tableNumber = beacon.tableNumber;
                    config.tableOnlyMode = beacon.orderType === BeaconOrderType.TABLE_ONLY || beacon.orderType === BeaconOrderType.TABLE_OR_PICKUP_ONLY;
                    config.pickupOnlyMode = beacon.orderType === BeaconOrderType.PICKUP_ONLY || beacon.orderType === BeaconOrderType.TABLE_OR_PICKUP_ONLY;
                    config.dealCode = beacon.dealCode;
                } else {
                    config.unknownBeacon = true;
                }
                config.beaconScanTime = Date.now();
            }

            if (!loggedIn) {
                this.authService.logout();
            }
        } catch (e) {
            console.error("Failed to setup config for beacon", e);
        }
    }

    async getConfig(): Promise<ParamConfig> {
        return this.config$.first().toPromise();
    }

    setConfig(config: ParamConfig): void {
        this.config$.next(config);
    }

    /**
     * Builds the URL to route to the specified external store
     * @param store The external store
     * @param orderType Current selected order type
     */
    async getURLForExternalStore(store: StoreData, orderType: OrderType): Promise<string> {
        let url: string;
        const config: ParamConfig = await this.getConfig();
        const externalStoreConfig: ParamConfig = config.cloneForExternalStore();
        if (store.parentAppStore) {
            url = config.parentAppURL;
            externalStoreConfig.store = store.externalStoreId;
        } else {
            url = store.externalAppURL;
            externalStoreConfig.parentAppURL = config.parentAppURL ? config.parentAppURL : window.location.href.split("?")[0];
            externalStoreConfig.store = store.externalStoreId;
        }
        if (orderType) {
            externalStoreConfig.orderType = orderType;
        }

        return url + (url.indexOf("?") === -1 ? "?" : "&") + "c=" + btoa(JSON.stringify(externalStoreConfig));
    }

    /**
     * Builds the URL to return to the parent app
     */
    async getURLForParentApp(): Promise<string> {
        let url: string;
        const config: ParamConfig = await this.getConfig();
        const parentAppConfig: ParamConfig = config.cloneForExternalStore();
        url = config.parentAppURL;
        parentAppConfig.store = undefined;
        return url + (url.indexOf("?") === -1 ? "?" : "&") + "c=" + btoa(JSON.stringify(parentAppConfig));
    }
}



// WEBPACK FOOTER //
// ./src/app/config-support/param-config.service.ts